import { useAuth0 } from '@auth0/auth0-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useApiAuth } from '../hooks/useApiAuth';
import { useUserRole } from '../hooks/useUserRole';

const navItems = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/sites', label: 'Sites' },
  { to: '/rule-severities', label: 'Rule Severity' },
  { to: '/blocks', label: 'Block Usage' },
  { to: '/ai-query', label: 'AI Query' },
  { to: '/webhooks', label: 'Webhooks' },
];

const adminNavItems = [{ to: '/admin', label: 'Admin' }];

export default function AppLayout() {
  const { user, logout } = useAuth0();
  // Injects the Auth0 token into the API client on mount and refresh
  const { isApiAuthReady } = useApiAuth();
  const userRole = useUserRole(isApiAuthReady);

  if (!isApiAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200">
          <h1 className="text-lg font-semibold text-gray-900">Website Builder <br />Audit Platform</h1>
          <p className="text-xs text-gray-500 mt-0.5">Site Quality &amp; Content Review</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* Admin section */}
          {userRole === 'admin' && (
            <>
              <div className="my-2 border-t border-gray-200" />
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin</p>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img src={user.picture} alt={user.name ?? ''} className="h-8 w-8 rounded-full" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name ?? 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email ?? ''}</p>
            </div>
          </div>
          <button
            onClick={() => void logout({ logoutParams: { returnTo: window.location.origin } })}
            className="mt-3 w-full text-left text-xs text-gray-500 hover:text-gray-900"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
